// Feature: M004-F0696 (M001-D033 §8 item 7). Test: T-M004-0170.
//
// Super-admin edit of one app key's registration declaration (label,
// description, login flag, default roles, status), opened from the app
// section heading of the role matrix. `defaultRoles` are chosen from the role
// columns the server returned; nothing is hard-coded. The write is
// PUT /v1/admin/applications/:appCode/permission-catalogue/:key (D033 §5); the
// page owns the request and the SAL-4 step-up and tells this drawer the outcome.
import React, { useState } from 'react';

const formFor = (key) => ({
  label: key.label,
  description: key.description,
  loginRequired: key.loginRequired,
  defaultRoles: Object.values(key.cells).filter((cell) => cell.default).map((cell) => cell.role),
  status: key.retired ? 'retired' : 'active',
  reason: '',
});

export default function AppPermissionDeclarationDrawer({ app, hidden, onSave, onClose }) {
  const [selectedName, setSelectedName] = useState(app.keys[0]?.name || '');
  const selected = app.keys.find((key) => key.name === selectedName) || app.keys[0];
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  if (!selected) return null;
  const form = forms[selected.name] || formFor(selected);
  const patch = (changes) => setForms((current) => ({ ...current, [selected.name]: { ...form, ...changes } }));
  // Core's closed schema: label 1-120, description 1-500, reason 3-500.
  const canSave = form.reason.trim().length >= 3 && form.label.trim().length > 0
    && form.description.trim().length > 0 && !saving;

  const save = async () => {
    setSaving(true);
    setMessage('');
    const outcome = await onSave(app.appCode, selected.key, {
      label: form.label.trim(),
      description: form.description,
      loginRequired: form.loginRequired,
      defaultRoles: form.defaultRoles,
      status: form.status,
      reason: form.reason.trim(),
    });
    setSaving(false);
    if (outcome?.status === 'error') setMessage(outcome.message || 'Save failed.');
  };

  return (
    <div className="drawer-overlay" hidden={hidden} data-testid="app-permission-declaration-drawer">
      <aside className="drawer" role="dialog" aria-label={`${app.appName} permission declaration`}>
        <header className="drawer-header">
          <h3>{app.appName} declaration</h3>
          <button type="button" className="btn btn-outline" onClick={onClose} data-testid="app-permission-declaration-close">Close</button>
        </header>
        <div className="drawer-body">
          <label htmlFor="appPermissionDeclarationKey">
            Permission
            <select
              id="appPermissionDeclarationKey"
              value={selected.name}
              onChange={(event) => { setSelectedName(event.target.value); setMessage(''); }}
              data-testid="app-permission-declaration-key"
            >
              {app.keys.map((key) => <option key={key.name} value={key.name}>{key.label}</option>)}
            </select>
          </label>
          <label htmlFor="appPermissionDeclarationLabel">
            Label
            <input
              id="appPermissionDeclarationLabel"
              value={form.label}
              onChange={(event) => patch({ label: event.target.value })}
              data-testid="app-permission-declaration-label"
            />
          </label>
          <label htmlFor="appPermissionDeclarationDescription">
            Description
            <textarea
              id="appPermissionDeclarationDescription"
              value={form.description}
              onChange={(event) => patch({ description: event.target.value })}
              data-testid="app-permission-declaration-description"
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.loginRequired}
              onChange={(event) => patch({ loginRequired: event.target.checked })}
              data-testid="app-permission-declaration-login"
            />
            Required to sign in to this application
          </label>
          <fieldset>
            <legend>Registration default roles</legend>
            {app.roles.map((role) => (
              <label key={role}>
                <input
                  type="checkbox"
                  checked={form.defaultRoles.includes(role)}
                  onChange={(event) => patch({
                    defaultRoles: event.target.checked
                      ? [...form.defaultRoles, role]
                      : form.defaultRoles.filter((value) => value !== role),
                  })}
                  data-testid={`app-permission-declaration-role-${role}`}
                />
                {role}
              </label>
            ))}
          </fieldset>
          <label htmlFor="appPermissionDeclarationStatus">
            Status
            <select
              id="appPermissionDeclarationStatus"
              value={form.status}
              onChange={(event) => patch({ status: event.target.value })}
              data-testid="app-permission-declaration-status"
            >
              <option value="active">Active</option>
              <option value="retired">Retired</option>
            </select>
          </label>
          <label htmlFor="appPermissionDeclarationReason">
            Reason
            <textarea
              id="appPermissionDeclarationReason"
              value={form.reason}
              onChange={(event) => patch({ reason: event.target.value })}
              placeholder="Why this declaration changes"
              data-testid="app-permission-declaration-reason"
            />
          </label>
          {message && <div className="permission-save-status error" role="alert" data-testid="app-permission-declaration-error">{message}</div>}
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSave}
            onClick={save}
            data-testid="app-permission-declaration-save"
          >
            {saving ? 'Saving...' : 'Save declaration'}
          </button>
        </div>
      </aside>
    </div>
  );
}
